import { ButtonBase, makeStyles } from '@material-ui/core';
import {
  mdiImageOffOutline,
  mdiImageOutline,
  mdiImageRemoveOutline,
  mdiVideoOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectPageRedesign } from 'components/ProjectPageRedesign/ProjectPageRedesignContext';
import {
  getRedesignDialogColors,
  getRedesignDialogFocusRing,
} from 'components/RedesignDialog';
import { BankVideoThumbnail } from 'scenes/ProjectBank/components/BankItems/BankVideoThumbnail';
import { useBankThumbnail } from 'scenes/ProjectBank/components/hooks/useBankThumbnail';
import { getRotatedThumbnailStyle } from 'utils/thumbnailRotation';

export const THUMB_SIZE = 36;

/** The activity-log target type of a gallery / bank image (VIEW_IMAGE rows). */
export const IMAGE_TARGET_TYPE = 'FILEPERMISSION';

/**
 * The name a row about an image calls it: as it is named now where the
 * viewer may see it, else as it was named when it was viewed. Never its id.
 * @param {Object} item the activity-log row
 * @param {Object} [image] the API's verdict on the image
 * @returns {string}
 */
export const getActivityImageName = (item, image) =>
  (image?.status === 'AVAILABLE' && image.name) || item.targetName || '';

/**
 * How far beyond the visible part of the list a thumbnail already counts as
 * on screen: a few rows, so they are there by the time they scroll in.
 */
export const THUMB_ROOT_MARGIN = '200px 0px';

const useStyles = makeStyles((theme) => ({
  box: {
    position: 'relative',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    flexShrink: 0,
    borderRadius: 4,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  media: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

// Inside a redesigned dialog (RedesignDialog provides the redesign): the
// dialog's well and muted glyph, its focus ring.
const useRedesignStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    return {
      box: {
        '&&': {
          backgroundColor: colors.well,
          color: colors.muted,
          borderRadius: 6,
          '&.Mui-focusVisible': getRedesignDialogFocusRing(colors),
        },
      },
    };
  },
  { name: 'LixActivityLogImageThumbRedesign' }
);

/**
 * Whether the element has come near the screen yet; once it has, stays so.
 * Without an IntersectionObserver (an old browser, jsdom) it counts as seen
 * at once.
 * @param {string} rootMargin
 * @returns {[Function, boolean]} the ref callback for the element, and seen
 */
export const useSeenOnce = (rootMargin = THUMB_ROOT_MARGIN) => {
  const [node, setNode] = useState(null);
  const [seen, setSeen] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof window.IntersectionObserver !== 'function'
  );

  useEffect(() => {
    if (seen || !node) return undefined;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, seen, rootMargin]);

  return [setNode, seen];
};

const PLACEHOLDER_ICONS = {
  DELETED: mdiImageRemoveOutline,
  FORBIDDEN: mdiImageOffOutline,
};

/**
 * The small picture of the image an activity-log row is about.
 *
 * An image the API says the viewer may see (`image.status === 'AVAILABLE'`)
 * shows its thumbnail, fetched through the project bank's thumbnail hook
 * (the files endpoint, shared URL cache) only once the row comes near the
 * screen, so a long day does not ask for every thumbnail at once; clicking
 * it calls `onOpen`. Anything else — deleted, not the viewer's to see, or not
 * resolved by the API — is a neutral glyph that opens nothing; the row's
 * text names the image. Inside a redesigned dialog (useProjectPageRedesign())
 * it takes the dialog's colours.
 */
function ActivityLogImageThumb({ filePermissionId, image, name, onOpen }) {
  const classes = useStyles();
  const redesignClasses = useRedesignStyles();
  const redesign = useProjectPageRedesign();
  const boxClassName = clsx(classes.box, redesign && redesignClasses.box);
  const { t } = useTranslation('common');
  const available = image?.status === 'AVAILABLE' && Boolean(filePermissionId);
  const isVideo =
    available && String(image.mimeType || '').startsWith('video/');
  const [ref, seen] = useSeenOnce();

  const thumbnail = useBankThumbnail(
    available && seen
      ? {
          thumbnail: {
            source: 'public',
            filePermissionId,
            ...(isVideo ? { media: 'video' } : {}),
          },
        }
      : null
  );

  if (!available) {
    const label =
      image?.status === 'DELETED'
        ? t('activity_log.image_deleted', 'Kuva on poistettu')
        : image?.status === 'FORBIDDEN'
          ? t(
              'activity_log.image_forbidden',
              'Sinulla ei ole oikeutta tähän kuvaan'
            )
          : undefined;
    return (
      <span
        className={boxClassName}
        title={label}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : 'true'}
        data-activity-log-thumb={image?.status || 'UNKNOWN'}
      >
        <Icon
          path={PLACEHOLDER_ICONS[image?.status] || mdiImageOutline}
          size={0.85}
        />
      </span>
    );
  }

  const ready = Boolean(thumbnail.src);
  let content;
  if (ready && isVideo) {
    content = (
      <BankVideoThumbnail
        className={classes.media}
        src={thumbnail.src}
        onError={thumbnail.onError}
      />
    );
  } else if (ready) {
    content = (
      <img
        className={classes.media}
        src={thumbnail.src}
        alt=""
        decoding="async"
        draggable={false}
        onError={thumbnail.onError}
        style={getRotatedThumbnailStyle(image.rotation)}
      />
    );
  } else {
    content = (
      <Icon path={isVideo ? mdiVideoOutline : mdiImageOutline} size={0.85} />
    );
  }

  return (
    <ButtonBase
      ref={ref}
      className={boxClassName}
      onClick={onOpen}
      title={name || undefined}
      aria-label={t('activity_log.open_image', {
        name: name || '',
        defaultValue: 'Avaa kuva {{name}}',
      }).trim()}
      data-activity-log-thumb={ready ? 'READY' : 'AVAILABLE'}
    >
      {content}
    </ButtonBase>
  );
}

ActivityLogImageThumb.propTypes = {
  filePermissionId: PropTypes.string,
  image: PropTypes.shape({
    status: PropTypes.oneOf(['AVAILABLE', 'DELETED', 'FORBIDDEN']),
    name: PropTypes.string,
    mimeType: PropTypes.string,
    rotation: PropTypes.number,
  }),
  name: PropTypes.string,
  onOpen: PropTypes.func,
};

export default ActivityLogImageThumb;
